import React, { useState, useEffect } from 'react';
import { Mail, Lock, User as UserIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { api } from '../lib/api';
import { User, Subscription } from '../../shared/types';

export interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (user: User, subscription: Subscription) => void;
}

export function GoogleIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
        fill="#EA4335"
      />
    </svg>
  );
}

export function AppleIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.62-.75 1.04-1.8 1.01-2.87-.89.04-1.98.6-2.61 1.34-.56.64-.97 1.7-1.01 2.76 1 .08 2-.49 2.61-1.23z" />
    </svg>
  );
}

export function AuthModalContent({
  onSuccess,
  onClose,
}: {
  onSuccess: (user: User, subscription: Subscription) => void;
  onClose: () => void;
}) {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isGoogleConfigured, setIsGoogleConfigured] = useState<boolean | null>(null);
  const [isAppleLoading, setIsAppleLoading] = useState(false);
  const [isAppleConfigured, setIsAppleConfigured] = useState<boolean | null>(null);
  const [verificationSent, setVerificationSent] = useState<{ email: string } | null>(null);

  // Check OAuth configuration status on mount
  useEffect(() => {
    async function checkConfigs() {
      try {
        const [gRes, aRes] = await Promise.allSettled([
          api.auth.google.getConfig(),
          api.auth.apple.getConfig(),
        ]);
        setIsGoogleConfigured(gRes.status === 'fulfilled' ? gRes.value.configured : false);
        setIsAppleConfigured(aRes.status === 'fulfilled' ? aRes.value.configured : false);
      } catch {
        setIsGoogleConfigured(false);
        setIsAppleConfigured(false);
      }
    }
    checkConfigs();
  }, []);

  // Check for error parameter in URL on mount (e.g. from OAuth redirect)
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const searchError = url.searchParams.get('error');
      const hashQuery = window.location.hash.includes('?') ? window.location.hash.split('?')[1] : '';
      const hashError = new URLSearchParams(hashQuery).get('error');
      const urlError = searchError || hashError;

      if (urlError) {
        if (urlError === 'apple_auth_failed' || urlError === 'invalid_code') {
          setError('Sign In with Apple could not be completed. Please try again or sign in with your email and password.');
        } else if (urlError === 'user_cancelled' || urlError === 'access_denied' || urlError === 'user_cancelled_authorize') {
          setError('Sign In with Apple was cancelled.');
        } else {
          setError(`Authentication error: ${urlError.replace(/_/g, ' ')}`);
        }
        // Clean URL params so error query doesn't persist
        const cleanHash = window.location.hash.split('?')[0] || '#/';
        window.history.replaceState(null, '', window.location.pathname + cleanHash);
      }
    } catch (_) {}
  }, []);

  // Handle Apple OAuth Login
  const handleAppleLogin = async () => {
    setError(null);
    setIsAppleLoading(true);

    try {
      if (isAppleConfigured === false) {
        throw new Error('Sign In with Apple is not configured yet on this server. Please sign in with Email & Password or configure Apple credentials in your environment.');
      }
      await api.auth.apple.signIn(window.location.origin + '/#/');
    } catch (err: any) {
      setError(err.message || 'Apple authentication failed');
      setIsAppleLoading(false);
    }
  };

  // Handle Google OAuth Login
  const handleGoogleLogin = async () => {
    setError(null);
    setIsGoogleLoading(true);

    try {
      if (isGoogleConfigured === false) {
        throw new Error('Google Sign-In is not configured yet. Please sign in with Email & Password or configure Google credentials in your environment.');
      }
      await api.auth.google.signIn(window.location.origin + '/#/');
    } catch (err: any) {
      setError(err.message || 'Google authentication failed');
      setIsGoogleLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      if (isLogin) {
        const res = await api.auth.login({ email, password });
        onSuccess(res.user, res.subscription);
        onClose();
      } else {
        const res = await api.auth.register({ email, password, name });
        if ((res as any).requiresEmailVerification || !res.token) {
          setVerificationSent({ email });
          return;
        }
        onSuccess(res.user, res.subscription);
        onClose();
      }
    } catch (err: any) {
      setError(err.message || 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (verificationSent) {
    return (
      <div className="text-center py-4 space-y-4">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/10 text-amber-500">
          <Mail className="h-7 w-7" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-foreground dark:text-white">Verify Your Email Address</h3>
          <p className="text-xs text-muted-foreground mt-2 max-w-sm mx-auto leading-relaxed">
            We've sent a verification link from <strong className="text-foreground dark:text-white">no-reply@maintenanceprep.app</strong> to:
          </p>
          <div className="font-semibold text-sm text-amber-600 dark:text-amber-400 mt-1 font-mono">
            {verificationSent.email}
          </div>
          <p className="text-xs text-muted-foreground mt-3 max-w-xs mx-auto">
            Please click the link in your email to activate your account. You can then sign in and start your practice exams.
          </p>
        </div>
        <div className="pt-2">
          <Button
            variant="secondary"
            size="md"
            onClick={() => {
              setVerificationSent(null);
              setIsLogin(true);
            }}
            className="w-full"
          >
            Back to Sign In
          </Button>
        </div>
      </div>
    );
  }

  const isSocialBusy = isAppleLoading || isGoogleLoading || isLoading;

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="destructive" title="Authentication Notice">
          {error}
        </Alert>
      )}

      {/* 1. Social Logins: Apple & Google */}
      <div className="space-y-2">
        <button
          type="button"
          onClick={handleAppleLogin}
          disabled={isSocialBusy}
          className="w-full relative flex items-center justify-center gap-3 rounded-lg border border-zinc-900 dark:border-white/20 bg-black text-white hover:bg-zinc-800 dark:bg-white dark:text-black dark:hover:bg-zinc-100 px-4 py-2.5 text-sm font-semibold shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-60 cursor-pointer group"
        >
          {isAppleLoading ? (
            <div className="h-4 w-4 rounded-full border-2 border-white/60 dark:border-black/60 border-t-transparent animate-spin" />
          ) : (
            <AppleIcon className="h-4 w-4 shrink-0 fill-current transition-transform group-hover:scale-110" />
          )}
          <span>{isLogin ? 'Sign in with Apple' : 'Sign up with Apple'}</span>
        </button>

        <button
          type="button"
          onClick={handleGoogleLogin}
          disabled={isSocialBusy}
          className="w-full relative flex items-center justify-center gap-3 rounded-lg border border-border dark:border-slate-700 bg-card dark:bg-slate-900/90 hover:bg-slate-50 dark:hover:bg-slate-800 hover:border-slate-400 dark:hover:border-slate-600 px-4 py-2.5 text-sm font-semibold text-foreground dark:text-white shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-60 cursor-pointer group"
        >
          {isGoogleLoading ? (
            <div className="h-4 w-4 rounded-full border-2 border-slate-400 border-t-transparent animate-spin" />
          ) : (
            <GoogleIcon className="h-4 w-4 shrink-0 transition-transform group-hover:scale-110" />
          )}
          <span>{isLogin ? 'Sign in with Google' : 'Sign up with Google'}</span>
        </button>
      </div>

      <div className="relative my-3">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border dark:border-slate-800" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-card px-2 text-muted-foreground font-mono text-[10px]">Or continue with email</span>
        </div>
      </div>

      {/* 2. Email / Password Form */}
      <form onSubmit={handleSubmit} className="space-y-3">
        {!isLogin && (
          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Full Name</label>
            <div className="relative">
              <UserIcon className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Alex Miller"
                className="w-full rounded-lg border border-border dark:border-slate-700 bg-background dark:bg-slate-900 py-2 pl-9 pr-3 text-sm text-foreground dark:text-white focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
              />
            </div>
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Email Address</label>
          <div className="relative">
            <Mail className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="technician@company.com"
              className="w-full rounded-lg border border-border dark:border-slate-700 bg-background dark:bg-slate-900 py-2 pl-9 pr-3 text-sm text-foreground dark:text-white focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Password</label>
          <div className="relative">
            <Lock className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <input
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full rounded-lg border border-border dark:border-slate-700 bg-background dark:bg-slate-900 py-2 pl-9 pr-3 text-sm text-foreground dark:text-white focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
            />
          </div>
        </div>

        <Button
          type="submit"
          variant="amber"
          size="md"
          isLoading={isLoading}
          className="w-full mt-2 text-sm font-semibold shadow-md shadow-amber-500/20"
        >
          {isLogin ? 'Sign In' : 'Create Account'}
        </Button>
      </form>

      {/* Switch between Sign In and Sign Up */}
      <div className="text-center pt-1">
        <button
          type="button"
          onClick={() => {
            setIsLogin(!isLogin);
            setError(null);
          }}
          className="text-xs text-muted-foreground hover:text-amber-600 dark:hover:text-amber-400 transition-colors"
        >
          {isLogin
            ? "Don't have an account? Create one"
            : 'Already have an account? Sign in'}
        </button>
      </div>
    </div>
  );
}
